import { defineStore } from 'pinia'
import { transformIncomingPrompt } from '@/utils/dataTransformers'
import { useNetworkStore } from '@/stores/network/store'
import { usePromptsStore } from '@/stores/prompts/store'
import * as Api from './api'
import { useNotificationStore } from '@/stores/notifications/store'
import {
  getNotification,
  selectNotification,
} from '@/stores/notifications/utils'
import {
  callWithMeta,
  createMeta,
  getMetaSuccess,
  waitFor,
  type Meta,
} from '@/utils/store'
import { getDownloadUrl } from './utils'
import type { JobResponse } from '../job/api'
import { useFeaturesStore } from '../features/store'

function extractTtsErrorMessage(error: unknown): string | undefined {
  const jobResult = (
    error as { jobResult?: { payload?: { result?: unknown } } }
  )?.jobResult
  const innerResult = jobResult?.payload?.result

  if (typeof innerResult !== 'string') {
    return undefined
  }

  try {
    const parsed = JSON.parse(innerResult) as { message?: string }
    return parsed.message
  } catch {
    return undefined
  }
}

type AudioFileParams = {
  type: Api.AudioFile['type']
  id: string | number
}

type State = {
  audioFile?: Api.AudioFile
  audioFileUrl?: string
  generateTtsJobStatus?: JobResponse['status']
  lastTtsSettings?: Api.TtsLastSettings
  meta: Meta<{
    audioFile: AudioFileParams | undefined
    load: AudioFileParams
    create: undefined
    update: AudioFileParams
    deletePrompt: AudioFileParams
    loadAudioFileUrl: AudioFileParams
    audioFileUrl: AudioFileParams
    loadAudioFileUrlInStore: AudioFileParams
    generateTts: AudioFileParams | undefined
    lastTtsSettings: undefined
  }>
}

const getDefaultState = (): State => ({
  audioFile: undefined,
  audioFileUrl: undefined,
  generateTtsJobStatus: undefined,
  lastTtsSettings: undefined,
  meta: createMeta([
    'audioFile',
    'create',
    'update',
    'deletePrompt',
    'loadAudioFileUrl',
    'audioFileUrl',
    'loadAudioFileUrlInStore',
    'generateTts',
    'lastTtsSettings',
  ]),
})

export const useAudioFileStore = defineStore('audioFile', {
  state: getDefaultState,

  actions: {
    RESET() {
      // Avoid memory leaks by revoking old file URLs
      if (this.audioFileUrl) {
        URL.revokeObjectURL(this.audioFileUrl)
      }

      this.audioFile = getDefaultState().audioFile
      this.audioFileUrl = getDefaultState().audioFileUrl
      this.generateTtsJobStatus = getDefaultState().generateTtsJobStatus
      this.lastTtsSettings = getDefaultState().lastTtsSettings
      this.meta.audioFile = getDefaultState().meta.audioFile
      this.meta.audioFileUrl = getDefaultState().meta.audioFileUrl
      this.meta.generateTts = getDefaultState().meta.generateTts
      this.meta.lastTtsSettings = getDefaultState().meta.lastTtsSettings
    },

    async load({ type, id }: AudioFileParams, { useCache = true } = {}) {
      const hasCached =
        ['pending', 'success'].includes(this.meta.audioFile.status) &&
        Number(id) === Number(this.meta.audioFile.params?.id) &&
        type === this.meta.audioFile.params?.type

      if (useCache && hasCached) {
        await waitFor(() => this.meta.audioFile.status !== 'pending')

        return
      }

      const params = { type, id }

      await callWithMeta({
        store: this,
        metaFields: { load: params, audioFile: params },
        apiCall: async () => await Api.loadPrompt(type, id),
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('audioFile')) {
            this.audioFile = transformIncomingPrompt(result.payload)

            this.loadAudioFileUrlInStore({ type, id }, { useCache })
          }
        },
        onError: ({ result, isLatest }) => {
          if (isLatest('load')) {
            useNetworkStore().HANDLE_LOADING_ERROR(result.payload)
          }
        },
      })
    },

    async reload() {
      if (
        this.meta.audioFile.status !== 'initial' &&
        this.meta.audioFile.params
      ) {
        await this.load(this.meta.audioFile.params, { useCache: false })
      }
    },

    async create(prompt: FormData) {
      const params = undefined

      const { result } = await callWithMeta({
        store: this,
        metaFields: { create: params, audioFile: params },
        apiCall: async () => await Api.createPrompt(prompt),
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('create')) {
            usePromptsStore().load({ useCache: false })
          }

          if (isLatest('audioFile')) {
            this.audioFile = result.payload

            this.meta.audioFile = getMetaSuccess({
              id: result.payload.id,
              type: result.payload.type,
            })
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async update({ type, id, data }: AudioFileParams & { data: FormData }) {
      const params = { id, type }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { update: params, audioFile: params },
        apiCall: async () => await Api.updatePrompt(type, id, data),
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('update')) {
            useNetworkStore().SET_RECORDING_STATUS(result.payload.status)

            usePromptsStore().load({ useCache: false })
          }

          if (isLatest('audioFile')) {
            this.reload()
          }
        },
        onError: ({ result, isLatest }) => {
          if (isLatest('update')) {
            useNetworkStore().SET_RECORDING_STATUS(result.payload?.status)
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async assignMusicOnHold({
      type,
      id,
      data,
    }: AudioFileParams & { data: { assigned: boolean } }) {
      const result = await Api.assignMusicOnHold(type, id, data)

      if (result.status === 'success') {
        usePromptsStore().SET_MUSIC_ON_HOLD(result.payload.id)
      } else {
        useNotificationStore().add(selectNotification(result.payload))

        throw result.payload
      }

      const updatedAudio = usePromptsStore().assignedMusicOnHold

      // TODO: check this, id does not exist on type string
      // @ts-ignore
      this.audioFile = updatedAudio.id

      usePromptsStore().load({ useCache: false })

      this.reload()
    },

    updateMusicOnHoldSelection(data: any) {
      this.audioFile = data

      usePromptsStore().SET_MUSIC_ON_HOLD(data)
    },

    async deletePrompt({ type, id }: AudioFileParams) {
      const params = { id, type }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { deletePrompt: params, audioFile: params },
        apiCall: async () => await Api.deletePrompt(type, id),
        onSuccess: ({ isLatest }) => {
          if (isLatest('deletePrompt')) {
            usePromptsStore().load({ useCache: false })
          }

          if (isLatest('audioFile')) {
            this.RESET()
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async loadAudioFileUrl({ type, id }: AudioFileParams) {
      const params = { id, type }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { loadAudioFileUrl: params },
        apiCall: async () => await Api.loadAudioFile(type, id),
      })

      if (result.status === 'success') {
        return getDownloadUrl(result.payload)
      } else {
        throw result.payload
      }
    },

    async loadAudioFileUrlInStore(
      { type, id }: AudioFileParams,
      { useCache = true } = {}
    ) {
      const hasCached =
        ['pending', 'success'].includes(this.meta.audioFileUrl.status) &&
        Number(id) === Number(this.meta.audioFileUrl.params?.id) &&
        type === this.meta.audioFileUrl.params?.type

      if (useCache && hasCached) {
        await waitFor(() => this.meta.audioFileUrl.status !== 'pending')

        return
      }

      const params = { id, type }

      await callWithMeta({
        store: this,
        metaFields: { loadAudioFileUrlInStore: params, audioFileUrl: params },
        apiCall: async () => await Api.loadAudioFile(type, id),
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('audioFileUrl')) {
            // Avoid memory leaks by revoking old file URLs
            if (this.audioFileUrl) {
              URL.revokeObjectURL(this.audioFileUrl)
            }

            this.audioFileUrl = getDownloadUrl(result.payload)
          }
        },
        onError: ({ isLatest, result }) => {
          if (isLatest('audioFileUrl')) {
            // Avoid memory leaks by revoking old file URLs
            if (this.audioFileUrl) {
              URL.revokeObjectURL(this.audioFileUrl)
            }

            this.audioFileUrl = undefined

            if ((result.payload as any)?.response?.status === 404) {
              useNotificationStore().add(
                getNotification('$promptNotFoundError')
              )
            } else {
              useNotificationStore().add(selectNotification(result.payload))
            }
          }
        },
      })
    },

    async generateTts({
      id,
      type,
      name,
      text,
      language,
      voice,
    }: {
      id: Api.AudioFile['id'] | undefined
      type: Api.AudioFile['type']
      name?: Api.AudioFile['name']
      text: Api.TtsMetadata['original_text']
      language: Api.TtsMetadata['language']
      voice: Api.TtsMetadata['voice']
    }) {
      this.generateTtsJobStatus = undefined

      const params = id ? { id, type } : undefined

      const { result } = await callWithMeta({
        store: this,
        metaFields: { generateTts: params, audioFile: params },
        apiCall: async ({ isLatest }) =>
          await Api.generateTts(
            {
              id,
              type,
              name,
              text,
              language,
              voice,
            },
            (jobStatus) => {
              if (isLatest('generateTts')) {
                this.generateTtsJobStatus = jobStatus
              }
            }
          ),
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('generateTts')) {
            this.generateTtsJobStatus = 'completed'

            usePromptsStore().load({ useCache: false })

            this.loadLastTtsSettings({ useCache: false })
          }

          if (isLatest('audioFile')) {
            this.load(
              { id: result.payload.id, type: result.payload.type },
              { useCache: false }
            )
          }
        },
        onError: ({ isLatest, result }) => {
          if (isLatest('generateTts')) {
            const ttsMessage = extractTtsErrorMessage(result.payload)

            if (ttsMessage?.startsWith('TTS quota would be exceeded.')) {
              useNotificationStore().add(getNotification('$ttsQuotaLimitError'))
            } else {
              useNotificationStore().add(selectNotification(result.payload))
            }
          }
        },
      })

      return result
    },

    async loadLastTtsSettings({ useCache = true } = {}) {
      // TODO: remove after BE goes live
      if (!useFeaturesStore().hasAudioFileTtsFeature) {
        return
      }

      const hasCached = ['pending', 'success'].includes(
        this.meta.lastTtsSettings.status
      )

      if (useCache && hasCached) {
        await waitFor(() => this.meta.lastTtsSettings.status !== 'pending')

        return
      }

      await callWithMeta({
        store: this,
        metaFields: { lastTtsSettings: undefined },
        apiCall: async () => await Api.loadLastTtsSettings(),
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('lastTtsSettings')) {
            this.lastTtsSettings = result.payload
          }
        },
        onError: ({ isLatest, result }) => {
          if (isLatest('lastTtsSettings')) {
            this.lastTtsSettings = undefined

            useNotificationStore().add(selectNotification(result.payload))
          }
        },
      })
    },
  },
})
